import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import { BRAND } from "../lib/site";

export default function Footer() {
  return (
    <footer
      className="bg-mag border-t border-[rgba(212,175,55,0.2)] relative overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <h2 className="font-brand text-5xl md:text-6xl text-gold leading-none">
              {BRAND.name}
            </h2>
            <p className="font-body text-[11px] tracking-[0.35em] uppercase text-pink-luxe mt-3">
              {BRAND.domain}
            </p>
            <p className="font-heading text-white/70 mt-6 max-w-md text-lg leading-relaxed">
              A sanctuary of artistry, safety and discreet luxury — sculpting
              confidence through Brazilian Butt Lift, breast enhancement and
              concierge medical recovery.
            </p>
            <p className="mt-8 text-white/60 text-xs tracking-[0.2em] uppercase">
              NPI{" "}
              <a
                href={BRAND.npiUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-npi-link"
                className="text-gold hover:text-pink-luxe inline-flex items-center gap-1"
              >
                {BRAND.npi}
                <ExternalLink size={12} />
              </a>
            </p>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <h4 className="font-heading text-gold text-2xl mb-5">Explore</h4>
            <ul className="space-y-3 text-white/70 font-body text-sm">
              <li><Link to="/about" className="hover:text-gold transition" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/services" className="hover:text-gold transition" data-testid="footer-link-services">Services</Link></li>
              <li><Link to="/recovery" className="hover:text-gold transition" data-testid="footer-link-recovery">Recovery Center</Link></li>
              <li><Link to="/gallery" className="hover:text-gold transition" data-testid="footer-link-gallery">Before &amp; After</Link></li>
              <li><Link to="/consultations" className="hover:text-gold transition" data-testid="footer-link-consultations">Consultations</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="font-heading text-gold text-2xl mb-5">Reach Us</h4>
            <ul className="space-y-4 text-white/75 font-body text-sm">
              {BRAND.offices.map((o, i) => (
                <li className="flex gap-3" key={i} data-testid={`footer-office-${i}`}>
                  <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                  <span>
                    <span className="block text-pink-luxe text-[10px] tracking-[0.3em] uppercase">{o.label}</span>
                    {o.line1}<br />
                    {o.line2}
                  </span>
                </li>
              ))}
              <li className="flex gap-3">
                <Phone size={16} className="text-gold mt-0.5 shrink-0" />
                <a href={`tel:+1${BRAND.phoneHref}`} className="hover:text-gold" data-testid="footer-phone">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-gold mt-0.5 shrink-0" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-gold break-all" data-testid="footer-email">
                  {BRAND.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Clock size={16} className="text-gold mt-0.5 shrink-0" />
                <span data-testid="footer-hours">
                  {BRAND.hours.map((h, i) => (
                    <span className="block" key={i}>
                      <span className="text-white/85">{h.days}</span>
                      <span className="text-white/55"> · {h.time}</span>
                    </span>
                  ))}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="gold-divider mt-14 mb-8" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/45 text-xs tracking-[0.2em] uppercase" data-testid="footer-copyright">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-6 text-xs tracking-[0.2em] uppercase">
            <Link to="/privacy" className="text-white/55 hover:text-gold" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/terms" className="text-white/55 hover:text-gold" data-testid="footer-link-terms">Terms of Service</Link>
            <Link to="/hipaa" className="text-white/55 hover:text-gold" data-testid="footer-link-hipaa">HIPAA Compliance</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
